
/**
 * Changes made:
 * - 2025-04-29: Fixed form structure to prevent nested forms
 * - 2025-04-30: Fixed import of ValuationResult (using named import)
 * - 2025-04-30: Fixed parameter types in onSubmit handler
 * - 2025-05-10: Updated import paths for refactored hooks
 * - 2025-05-15: Fixed onSubmit handler to properly use form.handleSubmit
 * - 2025-05-16: Fixed ValuationInput props to match types consistently
 * - 2025-05-17: Fixed type imports to use consistent ValuationFormData interface
 * - 2025-05-24: Fixed import paths and rendering issues to ensure content appears
 * - 2025-05-25: Fixed form submission handler to resolve type mismatch error
 * - 2025-05-26: Added onContinue handler to properly navigate after valuation
 * - 2025-07-23: Added loading state support for better UX
 * - 2025-09-20: Updated to match dark grey container design with exact specifications
 * - 2025-09-27: Added z-10 positioning to ensure form appears above background vector
 * - 2025-09-28: Complete redesign to match new card styling with zinc-700 background and proper positioning
 * - 2025-09-28: Removed absolute positioning to enable horizontal alignment with text content via flexbox
 * - 2025-10-01: Added mobile optimization with responsive layout using isMobile prop
 * - 2025-10-02: Updated mobile version background color to #454545
 * - 2025-10-03: Added tablet support with isTablet prop for iPad-optimized layout
 * - 2025-11-08: Integrated Cloudflare Turnstile CAPTCHA to prevent bot submissions
 * - 2025-11-09: Improved UX by separating verification and loading states, added graceful degradation
 */

import React, { useEffect } from 'react';
import ValuationInput from './ValuationInput';
import { ValuationResult } from './ValuationResult';
import { TurnstileWidget } from '@/components/security/TurnstileWidget';
import { useEnhancedValuationForm } from '@/hooks/valuation/useEnhancedValuationForm';
import { useValuationResultNavigation } from '@/components/hero/valuation/hooks/useValuationResultNavigation';
import { useValuationStore } from '@/hooks/store/useValuationStore';

interface ValuationFormProps {
  isMobile?: boolean;
  isTablet?: boolean;
}

export const ValuationForm = ({ isMobile = false, isTablet = false }: ValuationFormProps) => {
  const {
    form,
    isLoading,
    showDialog,
    setShowDialog,
    valuationResult,
    handleFormSubmit,
    resetForm,
    handleContinue,
    turnstileWidgetRef
  } = useEnhancedValuationForm();
  
  const { turnstileToken, setTurnstileToken, allowBypass } = useValuationStore();
  
  // Log to help debug rendering
  useEffect(() => {
    console.log("ValuationForm rendered with dialog state:", {
      showDialog,
      hasValuationResult: !!valuationResult,
      isLoading,
      resultDetails: valuationResult ? {
        make: valuationResult.make,
        model: valuationResult.model,
        vin: valuationResult.vin,
        reservePrice: valuationResult.reservePrice
      } : null
    });
  }, [showDialog, valuationResult, isLoading]);

  if (isMobile) {
    return (
      <div 
        data-layer="Card" 
        className="Card w-full px-6 py-6 bg-[#454545] rounded-2xl flex flex-col justify-start items-center gap-4 z-10"
      >
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className="w-full flex flex-col gap-4">
          <ValuationInput 
            form={form} 
            isLoading={isLoading} 
            waitingForVerification={!turnstileToken && !allowBypass}
            isMobile={true} 
          />
          <TurnstileWidget 
            ref={turnstileWidgetRef}
            onVerify={setTurnstileToken}
            onError={() => setTurnstileToken(null)}
            onExpire={() => setTurnstileToken(null)}
          />
        </form>

        {valuationResult && (
          <ValuationResult 
            open={showDialog}
            onOpenChange={setShowDialog}
            result={valuationResult}
            onReset={resetForm}
            onContinue={handleContinue}
          />
        )}
      </div>
    );
  }

  if (isTablet) {
    return (
      <div 
        data-layer="Card" 
        className="Card w-full max-w-[500px] px-8 py-8 bg-[#454545] rounded-2xl flex flex-col justify-start items-center gap-5 z-10"
      >
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className="w-full flex flex-col gap-5">
          <ValuationInput 
            form={form} 
            isLoading={isLoading} 
            waitingForVerification={!turnstileToken && !allowBypass}
            isTablet={true} 
          />
          <TurnstileWidget 
            ref={turnstileWidgetRef}
            onVerify={setTurnstileToken}
            onError={() => setTurnstileToken(null)}
            onExpire={() => setTurnstileToken(null)}
          />
        </form>

        {valuationResult && (
          <ValuationResult 
            open={showDialog}
            onOpenChange={setShowDialog}
            result={valuationResult}
            onReset={resetForm}
            onContinue={handleContinue}
          />
        )}
      </div>
    );
  }

  return (
    <div 
      data-layer="Card" 
      className="Card size-96 px-14 pt-9 pb-5 bg-zinc-700 rounded-3xl inline-flex flex-col justify-start items-center gap-5 overflow-hidden z-10"
    >
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="w-full flex flex-col" style={{ gap: '15px' }}>
        <ValuationInput 
          form={form} 
          isLoading={isLoading} 
          waitingForVerification={!turnstileToken && !allowBypass}
        />
        <TurnstileWidget 
          ref={turnstileWidgetRef}
          onVerify={setTurnstileToken}
          onError={() => setTurnstileToken(null)}
          onExpire={() => setTurnstileToken(null)}
        />
      </form>

      {valuationResult && (
        <ValuationResult 
          open={showDialog}
          onOpenChange={setShowDialog}
          result={valuationResult}
          onReset={resetForm}
          onContinue={handleContinue}
        />
      )}
    </div>
  );
};
